import React, { useState, useEffect } from "react"

export default function AskForsManager() {
  const [askFors, setAskFors] = useState([])
  const [shows, setShows] = useState([])
  const [games, setGames] = useState([])
  const [loading, setLoading] = useState(true)
  const [editingAskFor, setEditingAskFor] = useState(null)
  const [showForm, setShowForm] = useState(false)
  const [formData, setFormData] = useState({
    show_id: "",
    game_id: "",
    prompt: ""
  })

  useEffect(() => {
    fetchData()
  }, [])

  const fetchData = async () => {
    try {
      const [askForsRes, showsRes, gamesRes] = await Promise.all([
        fetch("/api/admin/ask_fors"),
        fetch("/api/admin/shows"),
        fetch("/api/admin/games")
      ])
      setAskFors(await askForsRes.json())
      setShows(await showsRes.json())
      setGames(await gamesRes.json())
      setLoading(false)
    } catch (error) {
      console.error("Error fetching data:", error)
      setLoading(false)
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    const url = editingAskFor
      ? `/api/admin/ask_fors/${editingAskFor.id}`
      : "/api/admin/ask_fors"
    const method = editingAskFor ? "PUT" : "POST"

    // Convert empty game_id to null
    const submitData = {
      ...formData,
      game_id: formData.game_id || null
    }

    try {
      const response = await fetch(url, {
        method,
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": document.querySelector("[name='csrf-token']").content
        },
        body: JSON.stringify({ ask_for: submitData })
      })

      if (response.ok) {
        fetchData()
        resetForm()
      } else {
        const data = await response.json()
        alert(`Error: ${data.errors?.join(", ")}`)
      }
    } catch (error) {
      console.error("Error saving ask for:", error)
      alert("Error saving ask for")
    }
  }

  const handleEdit = (askFor) => {
    setEditingAskFor(askFor)
    setFormData({
      show_id: askFor.show_id || "",
      game_id: askFor.game_id || "",
      prompt: askFor.prompt || ""
    })
    setShowForm(true)
  }

  const handleDelete = async (id) => {
    if (!confirm("Are you sure you want to delete this ask for?")) return

    try {
      await fetch(`/api/admin/ask_fors/${id}`, {
        method: "DELETE",
        headers: {
          "X-CSRF-Token": document.querySelector("[name='csrf-token']").content
        }
      })
      fetchData()
    } catch (error) {
      console.error("Error deleting ask for:", error)
      alert("Error deleting ask for")
    }
  }

  const resetForm = () => {
    setFormData({ show_id: "", game_id: "", prompt: "" })
    setEditingAskFor(null)
    setShowForm(false)
  }

  if (loading) return <div className="text-center py-8">Loading...</div>

  const getShowTitle = (showId) => shows.find(s => s.id === showId)?.title || "Unknown"
  const getGameType = (gameId) => games.find(g => g.id === gameId)?.game_type || "None"

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-semibold text-gray-800">Ask Fors Management</h2>
        <button
          onClick={() => setShowForm(!showForm)}
          className="bg-yellow-500 hover:bg-yellow-600 text-black px-4 py-2 rounded-lg font-semibold"
        >
          {showForm ? "Cancel" : "+ New Ask For"}
        </button>
      </div>

      {showForm && (
        <div className="bg-gray-50 p-6 rounded-lg mb-6">
          <h3 className="text-lg font-semibold mb-4">
            {editingAskFor ? "Edit Ask For" : "Create New Ask For"}
          </h3>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Show *
                </label>
                <select
                  required
                  value={formData.show_id}
                  onChange={(e) => setFormData({ ...formData, show_id: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500"
                >
                  <option value="">Select a show</option>
                  {shows.map((show) => (
                    <option key={show.id} value={show.id}>
                      {show.title}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Game (Optional)
                </label>
                <select
                  value={formData.game_id}
                  onChange={(e) => setFormData({ ...formData, game_id: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500"
                >
                  <option value="">No game</option>
                  {games.filter(g => g.show_id == formData.show_id).map((game) => (
                    <option key={game.id} value={game.id}>
                      {game.game_type} (Order: {game.order_in_show})
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Prompt *
              </label>
              <textarea
                required
                value={formData.prompt}
                onChange={(e) => setFormData({ ...formData, prompt: e.target.value })}
                rows="3"
                placeholder="What should we ask the audience for?"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500"
              />
            </div>
            <div className="flex gap-2">
              <button
                type="submit"
                className="bg-yellow-500 hover:bg-yellow-600 text-black px-6 py-2 rounded-lg font-semibold"
              >
                {editingAskFor ? "Update" : "Create"}
              </button>
              <button
                type="button"
                onClick={resetForm}
                className="bg-gray-300 hover:bg-gray-400 text-gray-800 px-6 py-2 rounded-lg font-semibold"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="space-y-4">
        {askFors.length === 0 ? (
          <p className="text-gray-500 text-center py-8">No ask fors yet.</p>
        ) : (
          askFors.map((askFor) => (
            <div
              key={askFor.id}
              className="bg-white border border-gray-200 rounded-lg p-6 hover:shadow-md transition-shadow"
            >
              <div className="flex justify-between items-start">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <h3 className="text-xl font-semibold text-gray-800">
                      {askFor.prompt}
                    </h3>
                  </div>
                  <div className="text-sm text-gray-600 space-y-1">
                    <p><span className="font-medium">Show:</span> {getShowTitle(askFor.show_id)}</p>
                    <p><span className="font-medium">Game:</span> {getGameType(askFor.game_id)}</p>
                  </div>
                </div>
                <div className="flex gap-2 ml-4">
                  <button
                    onClick={() => handleEdit(askFor)}
                    className="px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg font-semibold"
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => handleDelete(askFor.id)}
                    className="px-4 py-2 bg-red-500 hover:bg-red-600 text-white rounded-lg font-semibold"
                  >
                    Delete
                  </button>
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  )
}
